import {
  deriveScopeAppMix,
  type UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  deriveAppSplit,
  type UsageAreaDayRow,
} from "@/v2-backoffice-client/usage/usage.utils"
import { describeTrendSeasonNote } from "@/v2-backoffice-client/usage/usage.seasons"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import { derivePortfolioMixCounts } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import PortfolioMap from "@/v2-backoffice-client/portfolio/PortfolioMap"
import PortfolioSegments from "@/v2-backoffice-client/portfolio/PortfolioSegments"
import PortfolioIdleLicenses from "@/v2-backoffice-client/portfolio/PortfolioIdleLicenses"
import PortfolioEngagementBands from "@/v2-backoffice-client/portfolio/PortfolioEngagementBands"
import PortfolioMixSection from "@/v2-backoffice-client/portfolio/PortfolioMixSection"
import PortfolioAreaBreakdown from "@/v2-backoffice-client/portfolio/PortfolioAreaBreakdown"
import PortfolioAreaValue from "@/v2-backoffice-client/portfolio/PortfolioAreaValue"
import PortfolioLicenseDemandSection from "@/v2-backoffice-client/portfolio/PortfolioLicenseDemandSection"

export type PortfolioHealthSectionProps = {
  /** The globally filtered book — every diagnostic reads the same set. */
  customers: PortfolioCustomerSummary[]
  /** The book rolled up as one scope (shared with the Overview tab). */
  fleet: UsageScopeSummary
  /** Fleet daily rows — exact per-app trends for the product mix. */
  areaDays: UsageAreaDayRow[]
  /** Resolved server-side scope for the license-demand read; null = everyone. */
  customerIds: string[] | null
  excludeCustomerIds: string[]
  excludeUserIds: number[]
}

// The Health sub-tab: per-account diagnostics — the risk map, segments and
// idle licenses, the locked features people keep asking for, engagement
// bands and product mix, and the area value pair.
function PortfolioHealthSection(props: PortfolioHealthSectionProps) {
  const {
    customers,
    fleet,
    areaDays,
    customerIds,
    excludeCustomerIds,
    excludeUserIds,
  } = props

  const mixCounts = derivePortfolioMixCounts(customers)
  // Fleet product mix: volumes/seats from the members, exact per-app trends
  // from the daily rows (same recipe as the scope summaries) — always the
  // trailing 28 days anchored at now, like every other profile signal.
  const appSplit = deriveAppSplit(areaDays)
  const fleetAppMix = deriveScopeAppMix(
    customers.flatMap((customer) => customer.members),
    appSplit,
  )
  // Vacation-overlap note: the product-mix trends always compare
  // now-anchored trailing 28d pairs.
  const seasonNote28 = describeTrendSeasonNote(new Date(), 28)

  return (
    <div className="usage-report">
      <PortfolioMap
        customers={customers}
        className="h-[clamp(320px,50vh,660px)]"
      />
      <div className="grid items-start gap-x-8 gap-y-9 xl:grid-cols-2">
        <PortfolioSegments customers={customers} />
        <PortfolioIdleLicenses customers={customers} />
      </div>
      {/* The idle-licenses counterpart: paying without using above, using
          up to the lock without paying here (AIP-47). */}
      <PortfolioLicenseDemandSection
        customerIds={customerIds}
        excludeCustomerIds={excludeCustomerIds}
        excludeUserIds={excludeUserIds}
      />
      <div className="grid items-start gap-x-8 gap-y-9 xl:grid-cols-2">
        <PortfolioEngagementBands bands={fleet.seatBands} />
        <PortfolioMixSection
          entries={fleetAppMix}
          counts={mixCounts}
          trendSeasonNote={seasonNote28}
        />
      </div>
      {/* Unfiltered rows: the stacked bars themselves show the per-product
          split, which the overview's app chips would otherwise hide. */}
      <PortfolioAreaBreakdown areaDays={areaDays} customers={customers} />
      {/* The same capabilities, priced: usage says what the fleet does, this
          says what the money does. */}
      <PortfolioAreaValue customers={customers} />
    </div>
  )
}

export default PortfolioHealthSection
